import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { loadStripe } from '@stripe/stripe-js';
import { db } from '../../firebase'; // Adjust this import to match your firebase setup
import styles from './MyAccountPage.module.css';

const MyAccountPage = () => {
  const [user, setUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [activeTab, setActiveTab] = useState('profile');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  // Fetch user data from Firestore
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setIsLoading(true);
        
        // Get current user from Firebase Auth
        const auth = getAuth();
        const currentUser = auth.currentUser;
        
        if (!currentUser) {
          setError("You must be logged in to view account information");
          setIsLoading(false);
          return;
        }
        
        // Get user document from Firestore
        const userDoc = await getDoc(doc(db, "users", currentUser.uid));
        
        if (!userDoc.exists()) {
          setError("User profile not found");
          setIsLoading(false);
          return;
        }
        
        // Get user data from document and ensure subscription data exists
        const userData = userDoc.data();
        
        // Ensure subscription object exists to prevent crashes
        if (!userData.subscription) {
          userData.subscription = {
            plan: 'basic',
            expiresAt: null,
            storageUsed: 0,
            teamsCount: 0
          };
        }
        
        setUser(userData);
        setFormData({
          email: userData.email || '',
          firstName: userData.firstName || '',
          lastName: userData.lastName || ''
        });
        
        setIsLoading(false);
      } catch (err) {
        console.error("Error fetching user data:", err);
        setError("Failed to load account information. Please try again later.");
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      
      if (!currentUser) {
        setError("You must be logged in to update your profile");
        return;
      }
      
      // Update user document in Firestore
      await updateDoc(doc(db, "users", currentUser.uid), {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email
      });
      
      // Update local state
      setUser({
        ...user,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email
      });
      
      setIsEditing(false);
    } catch (err) {
      console.error("Error updating profile:", err);
      setError("Failed to update profile. Please try again.");
    }
  };

  const handleCancel = () => {
    // Reset form data to current user data
    setFormData({
      email: user.email || '',
      firstName: user.firstName || '',
      lastName: user.lastName || ''
    });
    setIsEditing(false);
  };

  const handleUpgradePlan = async () => {
    try {
      setIsProcessingPayment(true);
      const auth = getAuth();
      const currentUser = auth.currentUser;
      
      if (!currentUser) {
        setError("You must be logged in to upgrade your plan");
        setIsProcessingPayment(false);
        return;
      }
      
      // Get Firebase Functions instance
      const functions = getFunctions();
      
      // Create a reference to your callable function
      const createStripeSession = httpsCallable(functions, "createStripeSession");
      
      // Call the function with plan and userId
      const result = await createStripeSession({ 
        plan: "Pro", 
        userId: currentUser.uid 
      });
      
      const sessionId = result.data.id;
      console.log("Received session ID:", sessionId);
      
      // Load Stripe.js using your publishable key
      const stripe = await loadStripe(
        "pk_test_51Qzl4xB2xpuvoM49SPm8MOswuGnaNaQ2u8w8S6bKEukqjwH6USFNE070jjnCZq6W5QfnvHW7qcU0RLV0wkrbo1B800d4pkrwcl"
      );
      
      if (!stripe) {
        setError("Failed to load Stripe. Please refresh the page and try again.");
        setIsProcessingPayment(false);
        return;
      }
      
      // Redirect to Stripe Checkout
      const { error } = await stripe.redirectToCheckout({ sessionId });
      
      if (error) {
        console.error("Stripe redirection error:", error);
        setError("Payment processing failed. Please try again.");
        setIsProcessingPayment(false);
      }
      
    } catch (err) {
      console.error("Error upgrading plan:", err);
      setError("Failed to upgrade plan. Please try again.");
      setIsProcessingPayment(false);
    }
  };

  // Helper function to format date from Firestore timestamp
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    try {
      // Check if timestamp has toDate method (Firestore Timestamp)
      if (typeof timestamp.toDate === 'function') {
        return timestamp.toDate().toLocaleDateString();
      } 
      // Check if it's a date object
      else if (timestamp instanceof Date) {
        return timestamp.toLocaleDateString();
      }
      // If it's a string, return it directly
      else if (typeof timestamp === 'string') {
        return timestamp;
      }
      
      return 'N/A';
    } catch (err) {
      console.error("Error formatting date:", err);
      return 'N/A';
    }
  };

  // Display loading state
  if (isLoading) {
    return (
      <div className={styles.accountPage}>
        <h1 className={styles.pageTitle}>My Account</h1>
        <div className={styles.loadingIndicator}>
          <div className={styles.spinner}></div>
          Loading your account information...
        </div>
      </div>
    );
  }

  // Display error state
  if (error) {
    return (
      <div className={styles.accountPage}>
        <h1 className={styles.pageTitle}>My Account</h1>
        <div className={styles.errorMessage}>{error}</div>
        <button onClick={() => {
          setError(null);
          window.location.reload();
        }} className={styles.retryButton}>
          Retry
        </button>
      </div>
    );
  }

  // Ensure user data is loaded before rendering
  if (!user) {
    return null;
  }

  // Get plan name with capitalization
  const getCurrentPlan = () => {
    if (!user.subscription || !user.subscription.plan) return 'Basic';
    return user.subscription.plan.charAt(0).toUpperCase() + user.subscription.plan.slice(1);
  };

  const isPro = user.subscription && 
               (user.subscription.plan === 'pro' || user.subscription.plan === 'Pro');

  return (
    <div className={styles.accountPage}>
      <h1 className={styles.pageTitle}>My Account</h1>
      
      <div className={styles.tabs}>
        <button 
          className={`${styles.tabButton} ${activeTab === 'profile' ? styles.active : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          Profile
        </button>
        <button 
          className={`${styles.tabButton} ${activeTab === 'billing' ? styles.active : ''}`}
          onClick={() => setActiveTab('billing')}
        >
          Subscription
        </button>
        <button 
          className={`${styles.tabButton} ${activeTab === 'security' ? styles.active : ''}`}
          onClick={() => setActiveTab('security')}
        >
          Security
        </button>
      </div>

      <div className={styles.tabContent}>
        {activeTab === 'profile' && (
          <div className={styles.profileTab}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Profile Information</h2>
              {!isEditing && (
                <button className={styles.editButton} onClick={() => setIsEditing(true)}>
                  Edit Profile
                </button>
              )}
            </div>

            {isEditing ? (
              <form onSubmit={handleSubmit}>
                <div className={styles.formGroup}>
                  <label>First Name</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName || ''}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Last Name</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName || ''}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email || ''}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className={styles.formActions}>
                  <button type="submit" className={styles.saveButton}>Save Changes</button>
                  <button type="button" onClick={handleCancel} className={styles.cancelButton}>Cancel</button>
                </div>
              </form>
            ) : (
              <div className={styles.profileInfo}>
                <div className={styles.infoRow}>
                  <div className={styles.infoItem}>
                    <label>First Name</label>
                    <p>{user.firstName || 'Not set'}</p>
                  </div>
                  <div className={styles.infoItem}>
                    <label>Last Name</label>
                    <p>{user.lastName || 'Not set'}</p>
                  </div>
                </div>
                <div className={styles.infoRow}>
                  <div className={styles.infoItem}>
                    <label>Email</label>
                    <p>{user.email || 'Not set'}</p>
                  </div>
                  <div className={styles.infoItem}>
                    <label>Role</label>
                    <p>{user.role || 'Not set'}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'billing' && (
          <div className={styles.billingTab}>
            <h2 className={styles.sectionTitle}>Subscription Details</h2>
            
            <div className={styles.subscriptionInfo}>
              <div className={styles.planBox}>
                <div className={styles.planInfo}>
                  <h3 className={styles.subsectionTitle} data-plan={getCurrentPlan()}>
                    Current Plan: {getCurrentPlan()}
                  </h3>
                  {isPro ? (
                    <div className={styles.planFeatures}>
                      <p>Pro features include:</p>
                      <ul>
                        <li>Up to 5 teams with 100 players each</li>
                        <li>Enhanced announcements with media</li>
                        <li>Advanced player profiles with stats</li>
                        <li>Comprehensive scheduling with notifications</li>
                        <li>5GB media storage for photos and videos</li>
                        <li>Priority support (24-hour response)</li>
                      </ul>
                    </div>
                  ) : (
                    <div className={styles.planFeatures}>
                      <p>Basic plan features:</p>
                      <ul>
                        <li>1 team with up to 15 players</li>
                        <li>Basic announcements (text only)</li>
                        <li>Simple player roster management</li>
                        <li>Basic game scheduling</li>
                        <li>100MB photo storage</li>
                        <li>Email support (48-hour response)</li>
                      </ul>
                      <button 
                        className={styles.upgradeButton}
                        onClick={handleUpgradePlan}
                        disabled={isProcessingPayment}
                      >
                        {isProcessingPayment ? 'Processing...' : 'Upgrade to Pro - $9.99/month'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
              
              <div className={styles.subscriptionDetails}>
                <h3 className={styles.subsectionTitle}>Details</h3>
                <div className={styles.detailsItem}>
                  <label>Subscription Renews</label>
                  <p>{user.subscription ? formatDate(user.subscription.expiresAt) : 'N/A'}</p>
                </div>
                <div className={styles.detailsItem}>
                  <label>Storage Used</label>
                  <p>{user.subscription && typeof user.subscription.storageUsed === 'number' ? 
                    `${user.subscription.storageUsed} MB` : '0 MB'}</p>
                </div>
                <div className={styles.detailsItem}>
                  <label>Teams Count</label>
                  <p>{user.subscription && typeof user.subscription.teamsCount === 'number' ? 
                    user.subscription.teamsCount : '0'}</p>
                </div>
              </div>
              
              <div className={styles.paymentHistory}>
                <h3 className={styles.subsectionTitle}>Payment History</h3>
                <table className={styles.paymentTable}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Description</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isPro ? (
                      <>
                        <tr>
                          <td>Mar 1, 2025</td>
                          <td>Pro Subscription</td>
                          <td>$9.99</td>
                        </tr>
                        <tr>
                          <td>Feb 1, 2025</td>
                          <td>Pro Subscription</td>
                          <td>$9.99</td>
                        </tr>
                      </>
                    ) : (
                      <tr>
                        <td colSpan="3" className={styles.noPayments}>No payment history</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'security' && (
          <div className={styles.securityTab}>
            <h2 className={styles.sectionTitle}>Security Settings</h2>
            
            <div className={styles.passwordSection}>
              <h3 className={styles.subsectionTitle}>Change Password</h3>
              <form className={styles.passwordForm} onSubmit={(e) => {
                e.preventDefault();
                alert("Password change functionality would be implemented here");
                // In a real app, you would use Firebase Auth to update the password
                // For example:
                // const auth = getAuth();
                // const user = auth.currentUser;
                // const newPassword = "NEW_PASSWORD";
                // updatePassword(user, newPassword).then(() => {
                //   // Update successful
                // }).catch((error) => {
                //   // An error occurred
                // });
              }}>
                <div className={styles.formGroup}>
                  <label>Current Password</label>
                  <input type="password" required />
                </div>
                <div className={styles.formGroup}>
                  <label>New Password</label>
                  <input type="password" required />
                </div>
                <div className={styles.formGroup}>
                  <label>Confirm New Password</label>
                  <input type="password" required />
                </div>
                <button type="submit" className={styles.updatePasswordButton}>Update Password</button>
              </form>
            </div>
            
            <div className={styles.sessionsSection}>
              <h3 className={styles.subsectionTitle}>Active Sessions</h3>
              <div className={styles.sessionItem}>
                <div className={styles.sessionInfo}>
                  <p><strong>Current Session</strong></p>
                  <p>Device: {navigator.userAgent || 'Unknown Device'}</p>
                  <p>Last Active: Just now</p>
                </div>
                <div className={styles.sessionStatus}>Active</div>
              </div>
              <button className={styles.logoutButton} onClick={() => {
                // In a real app, you would sign out using Firebase Auth
                // For example:
                // const auth = getAuth();
                // signOut(auth).then(() => {
                //   // Sign-out successful, redirect to login
                //   window.location.href = '/login';
                // }).catch((error) => {
                //   // An error happened
                //   console.error('Sign out error:', error);
                // });
                alert("This would sign you out of all other devices");
              }}>Log Out All Other Devices</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyAccountPage;